import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { TONE_CLASS, TONE_VAR } from '../lib/status.ts';
import type { Tone } from '../lib/status.ts';

export interface GaugeProps {
  /** 0..1. Values outside are clamped. */
  value: number;
  /** bar: horizontal meter (default). ring: circular, for capacity at a glance. */
  variant?: 'bar' | 'ring';
  /** Fixed tone; by default it follows thresholds (calm below `warn`, waiting at `warn`, failed at `danger`). */
  tone?: Tone;
  warn?: number;
  danger?: number;
  /** Accessible name, for example "Memory". */
  label: string;
  /** Visible value text (defaults to a percentage). Ring: shown in the middle. */
  valueText?: ReactNode;
  showValue?: boolean;
  size?: number;
  className?: string;
}

/**
 * The fill of a bar meter as a transform, never a width: a full-width fill slid left by the
 * unfilled share. It runs on the compositor (no layout per update) and, unlike scaleX, keeps
 * the rounded end. The track clips it (overflow hidden).
 */
export function fillTransform(fraction: number): string {
  const f = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0));
  return `translateX(${Math.round((f - 1) * 10000) / 100 || 0}%)`;
}

function autoTone(v: number, warn: number, danger: number): Tone {
  return v >= danger ? 'failed' : v >= warn ? 'waiting' : 'accent';
}

/** A meter for load, memory and capacity. */
export function Gauge({
  value,
  variant = 'bar',
  tone,
  warn = 0.75,
  danger = 0.9,
  label,
  valueText,
  showValue = true,
  size = 44,
  className,
}: GaugeProps) {
  const v = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
  const t = tone ?? autoTone(v, warn, danger);
  const text = valueText ?? `${Math.round(v * 100)}%`;

  if (variant === 'ring') {
    const stroke = 4;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    return (
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(v * 100)}
        className={cn('relative inline-flex shrink-0 items-center justify-center', className)}
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-active)" strokeWidth={stroke} />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={TONE_VAR[t]}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${c * v} ${c}`}
            style={{ transition: 'stroke-dasharray var(--duration-slow) var(--easing-settle)' }}
          />
        </svg>
        {showValue && <span className="absolute text-xs font-medium text-fg num">{text}</span>}
      </div>
    );
  }

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(v * 100)}
        className="relative h-1.5 min-w-12 flex-1 overflow-hidden rounded-full bg-active"
      >
        <div
          data-fill=""
          className={cn('absolute inset-0 rounded-full transition-transform duration-(--duration-slow) ease-settle', TONE_CLASS[t].bg)}
          style={{ transform: fillTransform(v) }}
        />
      </div>
      {showValue && <span className="w-9 shrink-0 text-right text-sm text-fg-muted num">{text}</span>}
    </div>
  );
}
